import AddIcon from '@mui/icons-material/Add'
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye'
import SearchIcon from '@mui/icons-material/Search'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import baseUrl from '../../api/baseUrl'
import LabOrderDetailModal from '../../components/labs/LabOrderDetailModal'
import { LabStatusBadge } from '../../components/labs/LabOrderDetailView'
import {
  orderStatusStyles,
  paymentStatusStyles,
  formatLkr,
  orderMatchesKey,
  resolveOrderKey,
  type LabOrderDetail,
  type LabOrderListItem,
  type OrderStatus,
  type PaymentStatus,
} from '../../components/labs/labOrderTypes'

const ALL_STATUS = 'all'
const ALL_PAYMENT = 'all'

function ClinicLabOrders() {
  const [orders, setOrders] = useState<LabOrderListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [detailLoading, setDetailLoading] = useState(false)
  const [orderDetail, setOrderDetail] = useState<LabOrderDetail | null>(null)
  const [modalListOrder, setModalListOrder] = useState<LabOrderListItem | null>(null)
  const [modalOrderKey, setModalOrderKey] = useState<string | null>(null)
  const detailRequestRef = useRef(0)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState(ALL_STATUS)
  const [paymentFilter, setPaymentFilter] = useState(ALL_PAYMENT)

  const loadOrders = useCallback(() => {
    setLoading(true)
    baseUrl
      .get('client/labs/orders')
      .then((res) => setOrders(res.data.data ?? []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    loadOrders()
  }, [loadOrders])

  useEffect(() => {
    if (!modalOrderKey) {
      setOrderDetail(null)
      return
    }
    const requestId = ++detailRequestRef.current
    const controller = new AbortController()
    setDetailLoading(true)
    setOrderDetail(null)
    baseUrl
      .get(`client/labs/orders/${encodeURIComponent(modalOrderKey)}`, {
        signal: controller.signal,
      })
      .then((res) => {
        if (controller.signal.aborted || requestId !== detailRequestRef.current) return
        setOrderDetail((res.data.data ?? null) as LabOrderDetail | null)
      })
      .catch((err: { code?: string; name?: string }) => {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return
        setOrderDetail(null)
      })
      .finally(() => {
        if (!controller.signal.aborted) setDetailLoading(false)
      })
    return () => controller.abort()
  }, [modalOrderKey])

  const modalOrder =
    orderDetail && orderMatchesKey(orderDetail, modalOrderKey)
      ? orderDetail
      : null

  const modalOrderNumber = useMemo(() => {
    if (!modalOrderKey) return null
    return (
      modalOrder?.id ??
      orders.find(
        (o) =>
          o._id === modalOrderKey ||
          o.id === modalOrderKey ||
          resolveOrderKey(o) === modalOrderKey,
      )?.id ??
      null
    )
  }, [modalOrderKey, orders, modalOrder?.id])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return orders.filter((o) => {
      if (statusFilter !== ALL_STATUS && o.orderStatus !== statusFilter) {
        return false
      }
      if (paymentFilter !== ALL_PAYMENT && o.payment !== paymentFilter) {
        return false
      }
      if (!q) return true
      return (
        o.id.toLowerCase().includes(q) ||
        o.case.toLowerCase().includes(q) ||
        o.labName.toLowerCase().includes(q) ||
        (o.patient || '').toLowerCase().includes(q) ||
        (o.patientMeta || '').toLowerCase().includes(q)
      )
    })
  }, [orders, search, statusFilter, paymentFilter])

  const stats = useMemo(() => {
    const inProgress = orders.filter(
      (o) => o.orderStatus === 'In Progress' || o.orderStatus === 'Accepted',
    ).length
    const unpaid = orders.filter(
      (o) => o.payment === 'Unpaid' || o.payment === 'Partial',
    ).length
    const outstanding = orders.reduce(
      (sum, o) => sum + (o.balanceDueAmount ?? 0),
      0,
    )
    return {
      total: orders.length,
      inProgress,
      unpaid,
      outstanding,
    }
  }, [orders])

  function openOrderModal(order: LabOrderListItem) {
    const key = resolveOrderKey(order)
    if (!key) return
    setModalListOrder(order)
    setOrderDetail(null)
    setModalOrderKey(key)
  }

  function closeOrderModal() {
    setModalOrderKey(null)
    setModalListOrder(null)
    setOrderDetail(null)
  }

  function handleOrderUpdated(updated: LabOrderDetail) {
    if (orderMatchesKey(updated, modalOrderKey)) {
      setOrderDetail(updated)
    }
    setOrders((prev) =>
      prev.map((o) =>
        o._id === updated._id || o.id === updated.id
          ? {
              ...o,
              orderStatus: updated.orderStatus,
              payment: updated.payment,
              balanceDue:
                typeof updated.balanceDue === 'number'
                  ? formatLkr(updated.balanceDue)
                  : updated.balanceDue ?? o.balanceDue,
              balanceDueAmount:
                typeof updated.balanceDue === 'number'
                  ? updated.balanceDue
                  : updated.balanceDueAmount ?? o.balanceDueAmount,
              finalTotal:
                typeof updated.finalTotalAmount === 'number'
                  ? formatLkr(updated.finalTotalAmount)
                  : updated.finalTotal ?? o.finalTotal,
              isLocalTracking:
                updated.isLocalTracking ??
                (updated.trackingMode === 'local' ? true : o.isLocalTracking),
            }
          : o,
      ),
    )
  }

  return (
    <div className="flex min-h-[calc(100dvh-10rem)] w-full flex-col gap-6 p-4 md:p-6 lg:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100 md:text-xl">
            Lab orders
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Orders sent to partner labs and personal labs you track yourself.
          </p>
        </div>
        <Link
          to="/client/labs/send-order"
          className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-95"
        >
          <AddIcon sx={{ fontSize: 18 }} />
          Send new order
        </Link>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Total orders
          </p>
          <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">
            {stats.total}
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            In progress
          </p>
          <p className="mt-1 text-2xl font-bold text-amber-600">{stats.inProgress}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Awaiting payment
          </p>
          <p className="mt-1 text-2xl font-bold text-red-600">{stats.unpaid}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Balance due (all)
          </p>
          <p className="mt-1 text-lg font-bold text-primary">
            LKR {stats.outstanding.toLocaleString('en-LK')}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative min-w-[200px] flex-1 sm:max-w-md">
          <SearchIcon
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            sx={{ fontSize: 20 }}
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search order #, case, lab, patient…"
            className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 text-sm dark:border-slate-600 dark:bg-slate-800"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm dark:border-slate-600 dark:bg-slate-800"
        >
          <option value={ALL_STATUS}>All statuses</option>
          {(
            [
              'Pending',
              'Accepted',
              'In Progress',
              'Completed',
              'Cancelled',
            ] as OrderStatus[]
          ).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={paymentFilter}
          onChange={(e) => setPaymentFilter(e.target.value)}
          className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm dark:border-slate-600 dark:bg-slate-800"
        >
          <option value={ALL_PAYMENT}>All payments</option>
          {(['Unpaid', 'Partial', 'Settled'] as PaymentStatus[]).map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
        {loading ? (
          <p className="p-10 text-center text-sm text-slate-500">Loading orders…</p>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-sm text-slate-500">
              {orders.length === 0
                ? 'No orders sent yet.'
                : 'No orders match your filters.'}
            </p>
            {orders.length === 0 ? (
              <Link
                to="/client/labs/send-order"
                className="mt-4 inline-flex text-sm font-medium text-primary hover:underline"
              >
                Send your first lab order
              </Link>
            ) : null}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/90 dark:border-slate-700 dark:bg-slate-800/80">
                  <th className="px-4 py-3 font-medium text-slate-500">Order</th>
                  <th className="px-4 py-3 font-medium text-slate-500">Lab</th>
                  <th className="px-4 py-3 font-medium text-slate-500">Case</th>
                  <th className="px-4 py-3 font-medium text-slate-500">Patient</th>
                  <th className="px-4 py-3 font-medium text-slate-500">Order date</th>
                  <th className="px-4 py-3 font-medium text-slate-500">Due</th>
                  <th className="px-4 py-3 font-medium text-slate-500">Items</th>
                  <th className="px-4 py-3 font-medium text-slate-500">Total</th>
                  <th className="px-4 py-3 font-medium text-slate-500">Balance</th>
                  <th className="px-4 py-3 font-medium text-slate-500">Status</th>
                  <th className="px-4 py-3 font-medium text-slate-500">Payment</th>
                  <th className="px-4 py-3 font-medium text-slate-500 text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <tr
                    key={resolveOrderKey(o)}
                    className="border-b border-slate-100 last:border-b-0 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/50"
                  >
                    <td className="px-4 py-3 font-medium text-primary">
                      #{o.id}
                    </td>
                    <td className="px-4 py-3 text-slate-800 dark:text-slate-200">
                      {o.labName}
                      {o.isLocalTracking || o.trackingMode === 'local' ? (
                        <span className="ml-2 inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                          Self-tracked
                        </span>
                      ) : null}
                    </td>
                    <td className="max-w-[180px] truncate px-4 py-3 text-slate-700 dark:text-slate-300">
                      {o.case}
                    </td>
                    <td className="max-w-[160px] px-4 py-3">
                      <span className="block truncate font-medium text-slate-800 dark:text-slate-200">
                        {o.patient || '—'}
                      </span>
                      {o.patientMeta ? (
                        <span className="block truncate text-xs text-slate-500">
                          {o.patientMeta}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                      {o.orderDate || '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                      {o.dueDate || '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-center">
                      {o.itemCount ?? 0}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100 whitespace-nowrap">
                      {o.finalTotal}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={
                          (o.balanceDueAmount ?? 0) > 0
                            ? 'font-medium text-red-600'
                            : 'text-slate-500'
                        }
                      >
                        {o.balanceDue ?? o.finalTotal}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <LabStatusBadge
                        label={o.orderStatus}
                        className={orderStatusStyles[o.orderStatus]}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <LabStatusBadge
                        label={o.payment}
                        className={paymentStatusStyles[o.payment]}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => openOrderModal(o)}
                        className="inline-flex size-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 transition-colors hover:bg-indigo-100 dark:bg-indigo-900/40 dark:text-indigo-300 dark:hover:bg-indigo-900/60"
                        title="View order details"
                        aria-label={`View order ${o.id} details`}
                      >
                        <RemoveRedEyeIcon sx={{ fontSize: 18 }} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <LabOrderDetailModal
        key={modalOrderKey ?? 'closed'}
        open={modalOrderKey != null}
        orderId={modalOrderKey}
        orderNumber={modalOrderNumber}
        listOrder={modalListOrder}
        order={modalOrder}
        loading={detailLoading}
        onClose={closeOrderModal}
        onOrderUpdated={handleOrderUpdated}
      />
    </div>
  )
}

export default ClinicLabOrders
